import { Injectable, signal, computed } from '@angular/core';
import type { DrawerRef } from './drawer.types';

@Injectable({
  providedIn: 'root',
})
export class DrawerService {
  readonly #openDrawers = signal<Map<string, DrawerRef>>(new Map());
  readonly #drawerCounter = signal(0);

  readonly openDrawerCount = computed(() => this.#openDrawers().size);
  readonly hasOpenDrawers = computed(() => this.openDrawerCount() > 0);

  /**
   * Register a drawer instance with the service
   * @param drawer The drawer reference to register
   * @returns A unique ID for the drawer
   */
  register(drawer: DrawerRef): string {
    const id = `drawer-${this.#drawerCounter()}`;
    this.#drawerCounter.set(this.#drawerCounter() + 1);

    this.#openDrawers.update((drawers) => {
      const newDrawers = new Map(drawers);
      newDrawers.set(id, drawer);
      return newDrawers;
    });

    return id;
  }

  /**
   * Unregister a drawer instance from the service
   * @param id The drawer ID to unregister
   */
  unregister(id: string): void {
    this.#openDrawers.update((drawers) => {
      const newDrawers = new Map(drawers);
      newDrawers.delete(id);
      return newDrawers;
    });
  }

  /**
   * Open a specific drawer by ID
   * @param id The drawer ID to open
   */
  open(id: string): void {
    const drawer = this.#openDrawers().get(id);
    if (drawer) {
      drawer.open();
    }
  }

  /**
   * Close a specific drawer by ID
   * @param id The drawer ID to close
   */
  close(id: string): void {
    const drawer = this.#openDrawers().get(id);
    if (drawer) {
      drawer.close();
    }
  }

  /**
   * Toggle a specific drawer by ID
   * @param id The drawer ID to toggle
   */
  toggle(id: string): void {
    const drawer = this.#openDrawers().get(id);
    if (drawer) {
      drawer.toggle();
    }
  }

  /**
   * Close all open drawers
   */
  closeAll(): void {
    for (const drawer of this.#openDrawers().values()) {
      drawer.close();
    }
  }

  /**
   * Get the current state of a drawer
   * @param id The drawer ID to check
   * @returns True if the drawer is open, false otherwise
   */
  isOpen(id: string): boolean {
    const drawer = this.#openDrawers().get(id);
    return drawer ? drawer.isOpen() : false;
  }

  /**
   * Get all registered drawer IDs
   * @returns Array of drawer IDs
   */
  getDrawerIds(): string[] {
    return Array.from(this.#openDrawers().keys());
  }
}
